import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CustomError, MemberCodeStatus } from 'types';
import { getMemberCode } from 'services/member-code/getMemberCode';
import { generateEmail } from 'services/member-code/generateEmail';
import { GenerateEmailResponse } from 'pco-product-service-internal-consumer';
import { GenerateEmailRequest } from 'pco-product-service-internal-consumer';
import { closeModal, openModal } from './modalReducer';

export const initialMemberCodeStatus: MemberCodeStatus = {
  isLoading: true,
  isGenerateCodeLoading: false,
  fetchCodeError: undefined,
  generateCodeError: undefined,
  distributionRequest: undefined,
};

export const generateMemberCodeThunk = createAsyncThunk(
  'MEMBER_CODE/generateCode',
  async (request: { partnerNumber: string; requestBody: GenerateEmailRequest }, thunkAPI) => {
    const { partnerNumber, requestBody } = request;
    try {
      return await generateEmail(partnerNumber, requestBody);
    } catch (error: unknown) {
      thunkAPI.dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              thunkAPI.dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when generating email',
              userErrorMessage: `An error occurred when generating email. Please try again later.`,
            },
          },
        }),
      );
    }
  },
);

export const getMemberCodeThunk = createAsyncThunk(
  'MEMBER_CODE/getMemberCode',
  async (partnerNumber: string, thunkAPI) => {
    try {
      return await getMemberCode(partnerNumber);
    } catch (error: unknown) {
      thunkAPI.dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              thunkAPI.dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when retrieving member code',
              userErrorMessage: `An error occurred when retrieving member code. Please try again later.`,
            },
          },
        }),
      );
    }
  },
);

const memberCodeStatusSlice = createSlice({
  name: 'MEMBER_CODE_STATUS',
  initialState: initialMemberCodeStatus,
  reducers: {
    setIsCodeLoading: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoading: action.payload };
    },
    setFetchMemberCodeError: (state, action: PayloadAction<CustomError | undefined>) => {
      return { ...state, fetchCodeError: action.payload };
    },
    setGenerateCodeError: (state, action: PayloadAction<CustomError | undefined>) => {
      return { ...state, generateCodeError: action.payload };
    },
    setDistributionRequest: (state, action: PayloadAction<GenerateEmailResponse | undefined>) => {
      return { ...state, distributionRequest: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(generateMemberCodeThunk.pending, (state) => {
      return { ...state, isGenerateCodeLoading: true };
    });
    builder.addCase(generateMemberCodeThunk.fulfilled, (state, action) => {
      return {
        ...state,
        isGenerateCodeLoading: false,
        distributionRequest: action.payload,
      };
    });
    builder.addCase(generateMemberCodeThunk.rejected, (state, action) => {
      return {
        ...state,
        isGenerateCodeLoading: false,
        generateCodeError: action.payload as CustomError,
      };
    });

    builder.addCase(getMemberCodeThunk.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(getMemberCodeThunk.fulfilled, (state) => {
      return { ...state, isLoading: false };
    });
    builder.addCase(getMemberCodeThunk.rejected, (state, action) => {
      return {
        ...state,
        isLoading: true,
        fetchCodeError: action.payload as CustomError,
      };
    });
  },
});

export const { setIsCodeLoading, setFetchMemberCodeError, setGenerateCodeError, setDistributionRequest } =
  memberCodeStatusSlice.actions;
export default memberCodeStatusSlice.reducer;
